// Single owner of the video-block URL rules: what counts as an allowed video URL,
// and how that URL maps to an embeddable player URL.
//
// Shared by the Consultation Manual (PRD 11), Management Guidelines (PRD 07) and
// Health Encyclopedia (PRD 10) — editors, validators and previews alike — so all
// three modules accept the same set and render video identically (ADM.044 "same
// rendering across all modules").
//
// SHCT-467-adjacent QC find (관리지침 video block): these rules used to live in FIVE
// hand-copied places that had drifted into three different behaviors. A legit link
// (`https://player.vimeo.com/video/76979871`) was flagged "허용되지 않은 링크입니다" by
// the Management Guidelines editor — its private copy matched the host EXACTLY
// against a list with no `player.vimeo.com` — while the preview beside it happily
// embedded the same URL, because the preview matched by regex. Validation and
// preview now come from this one module; a host either passes both or fails both.

// A video URL must be `https://` and point at YouTube or Vimeo. A host qualifies when
// it EQUALS one of these bases or is a sub-domain of one — `www.`, `m.`, `music.` and
// `player.` are all in scope. This mirrors the backend allow-list exactly, so the FE
// never rejects a URL the backend would accept, nor accepts one it would reject.
//
// Deliberately NOT listed: `youtube-nocookie.com`. It is YouTube's own privacy-preserving
// player domain, but it is a separate base domain, so the sub-domain rule cannot reach it
// and it would have to be named here in its own right. The requirement names YouTube and
// Vimeo without naming this domain, and the decision taken was to keep refusing it rather
// than widen the rule. Adding it later means changing this list AND `toVideoEmbedUrl`
// below AND the private copies in the patient and medical clients — miss any one and an
// author saves a link some reader cannot play.
const VIDEO_HOSTS = ['youtube.com', 'youtu.be', 'vimeo.com'] as const;

/**
 * True when `url` is an acceptable video-block URL: `https://` + a YouTube/Vimeo host
 * (or a sub-domain of one). An empty/blank URL is NOT valid here — "required" is the
 * form-level check's job, so callers must skip blanks before asking.
 */
export function isAllowedVideoUrl(url: string | null | undefined): boolean {
  const trimmed = (url ?? '').trim();
  if (!/^https:\/\//i.test(trimmed)) return false;
  let host: string;
  try {
    host = new URL(trimmed).hostname.toLowerCase();
  } catch {
    return false;
  }
  return VIDEO_HOSTS.some((base) => host === base || host.endsWith(`.${base}`));
}

/**
 * Map an allowed video URL to its embeddable player URL. Returns '' when the URL is
 * not allowed, or is allowed but carries no extractable video id — callers fall back
 * to rendering a plain link.
 *
 * ADM.014 spells out the YouTube forms (standard `watch?v=`, short `youtu.be/`, embed
 * `/embed/`, with query parameters) and leaves Vimeo open — "유효한 Vimeo URL이면 별도
 * 형식 제한 없음". So Vimeo is matched by its numeric video id in the LAST numeric path
 * segment, which covers every shape Vimeo publishes: `vimeo.com/ID`, `vimeo.com/video/ID`,
 * `player.vimeo.com/video/ID`, `vimeo.com/channels/<name>/ID`, `vimeo.com/groups/<g>/videos/ID`.
 *
 * SHCT-872: an UNLISTED video carries a privacy hash alongside its id — as a trailing path
 * segment (`vimeo.com/873265414/0894c5a3b6`) or as `?h=` on a player URL. The hash is not
 * decoration: the player refuses to play the id on its own, so dropping it turned a link the
 * author saved without warning into a dead frame for every reader. It is carried through to
 * the embed URL as `?h=`.
 */
export function toVideoEmbedUrl(url: string | null | undefined): string {
  const trimmed = (url ?? '').trim();
  if (!isAllowedVideoUrl(trimmed)) return '';

  // YouTube: youtu.be/ID, /embed/ID, watch?v=ID (v may not be the first param). These patterns must
  // stay in step with VIDEO_HOSTS above: a host this module accepts but no pattern here recognises
  // would validate fine and then render as a bare link, which is the validation-versus-preview split
  // this module exists to remove. Extend both together or neither.
  const yt =
    trimmed.match(/youtu\.be\/([\w-]{11})/) ??
    trimmed.match(/youtube\.com\/embed\/([\w-]{11})/) ??
    trimmed.match(/youtube\.com\/watch\?(?:[^#]*&)?v=([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;

  // Vimeo: the id is the last all-numeric path segment, whatever the surrounding path.
  // Gated on a Vimeo host so a YouTube URL in an unrecognized shape (say
  // `youtube.com/playlist?list=…`) falls through to the plain-link fallback instead of
  // having a stray numeric path segment mistaken for a Vimeo id.
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return '';
  }
  const host = parsed.hostname.toLowerCase();
  if (host !== 'vimeo.com' && !host.endsWith('.vimeo.com')) return '';

  const segments = parsed.pathname.split('/').filter(Boolean);
  const unlisted = unlistedPair(segments);
  const vimeoId = unlisted ? unlisted.id : segments.filter((seg) => /^\d+$/.test(seg)).pop();
  if (!vimeoId) return '';

  // A hash in the path and a hash in `?h=` are the same secret in two notations; either alone
  // is enough, and the path form wins when a URL somehow carries both.
  const hash = unlisted?.hash ?? queryHash(parsed);
  const embed = `https://player.vimeo.com/video/${vimeoId}`;
  return hash ? `${embed}?h=${hash}` : embed;
}

/**
 * The unlisted shapes `vimeo.com/<id>/<hash>` and `vimeo.com/video/<id>/<hash>`, and only those:
 * the id/hash pair must be the WHOLE path (bar a leading `video`). Matching a trailing pair
 * anywhere would misread `vimeo.com/channels/<numeric channel>/<id>` — a real Vimeo address —
 * as an id followed by a hash, and embed the channel instead of the video. Returns null for
 * every other path, which then falls back to the last-numeric-segment rule.
 */
function unlistedPair(segments: string[]): { id: string; hash: string } | null {
  const path = segments[0]?.toLowerCase() === 'video' ? segments.slice(1) : segments;
  if (path.length !== 2) return null;
  const [id, hash] = path;
  // Vimeo's privacy hash is a short hex token; the length floor keeps a genuine second
  // numeric segment (a 4-digit channel, say) from passing as one.
  if (!/^\d+$/.test(id) || !/^[0-9a-f]{6,16}$/i.test(hash)) return null;
  return { id, hash };
}

/** The `?h=` privacy hash of a player URL, '' when absent or not hash-shaped. */
function queryHash(parsed: URL): string {
  const h = parsed.searchParams.get('h') ?? '';
  return /^[0-9a-f]{6,16}$/i.test(h) ? h : '';
}
